import { useEffect, useState } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { X, Sparkles } from 'lucide-react';

const DISMISS_KEY = 'bp_survey_dismissed_at';
const DISMISS_HOURS = 14 * 24; // align with server-side snooze (14 days) after a dismiss

export default function SurveyModal() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [score, setScore] = useState(null);
  const [q2, setQ2] = useState('');
  const [q3, setQ3] = useState('');
  const [q4, setQ4] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [thanks, setThanks] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get('/survey/status');
        setStatus(data);
        if (!data.eligible) return;
        // Respect a local dismiss lockout of DISMISS_HOURS
        const lastDismiss = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
        if (lastDismiss && Date.now() - lastDismiss < DISMISS_HOURS * 60 * 60 * 1000) return;
        // Delay showing for 4s so user isn't ambushed
        setTimeout(() => setOpen(true), 4000);
      } catch { /* ignore */ }
    })();
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
    // Persist server-side so it stays dismissed across devices / cache clears.
    http.post('/survey/dismiss').catch(() => {});
  };

  const submit = async () => {
    if (score === null) { toast.error('Pick a score first'); return; }
    setSubmitting(true);
    try {
      await http.post('/survey/submit', {
        nps_score: score,
        most_helped: q2 || undefined,
        almost_stopped: q3 || undefined,
        would_make_better: q4 || undefined,
      });
      setThanks(true);
      toast.success('Thank you — this shapes what we build next.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !status?.eligible) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end lg:items-center justify-center p-0 lg:p-6" data-testid="survey-modal">
      <div className="w-full lg:max-w-xl bg-white rounded-t-3xl lg:rounded-3xl p-6 lg:p-10 shadow-2xl animate-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold inline-flex items-center gap-1">
              <Sparkles size={11}/> Weekly Pulse
            </p>
            <h2 className="font-heading text-2xl lg:text-3xl text-navy mt-1">4 quick questions.</h2>
            <p className="text-sm text-navy/60 mt-1">You&apos;ve been with us a week. Your honest signal helps us build what actually matters.</p>
          </div>
          <button data-testid="survey-close" onClick={dismiss} className="text-navy/40 hover:text-navy p-1">
            <X size={20}/>
          </button>
        </div>

        {thanks ? (
          <div className="text-center py-8" data-testid="survey-thanks">
            <div className="w-14 h-14 mx-auto rounded-full bg-green/10 flex items-center justify-center mb-3">
              <Sparkles className="text-green" size={26} strokeWidth={1.5}/>
            </div>
            <p className="font-heading text-xl">Thank you, brother.</p>
            <p className="text-sm text-navy/60 mt-1">We read every response.</p>
            <button onClick={() => setOpen(false)} className="mt-6 bg-navy text-white rounded-full px-6 py-3 text-sm">Close</button>
          </div>
        ) : (
          <>
            {/* Q1: NPS */}
            <div className="mt-2">
              <p className="text-sm lg:text-base font-medium text-navy leading-snug">
                1. How likely are you to recommend Brotherhood+ to another man?
              </p>
              <p className="text-xs text-navy/50 mt-1">0 = not at all likely · 10 = extremely likely</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {Array.from({ length: 11 }, (_, i) => (
                  <button
                    key={i}
                    data-testid={`survey-nps-${i}`}
                    onClick={() => setScore(i)}
                    className={`w-10 h-10 lg:w-11 lg:h-11 rounded-full text-sm font-medium transition-smooth ${
                      score === i
                        ? 'bg-navy text-white ring-2 ring-gold ring-offset-2'
                        : i <= 6 ? 'bg-red-50 text-red-700 hover:bg-red-100'
                        : i <= 8 ? 'bg-beige text-navy hover:bg-[#EAE5D9]'
                        : 'bg-green/10 text-green hover:bg-green/20'
                    }`}
                  >{i}</button>
                ))}
              </div>
            </div>

            {/* Q2 */}
            <div className="mt-5">
              <label className="text-sm lg:text-base font-medium text-navy block leading-snug">
                2. What has helped you the most?
              </label>
              <textarea
                data-testid="survey-q2"
                value={q2}
                onChange={(e) => setQ2(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Optional — a lesson, the AI Coach, the check-ins…"
                className="mt-2 w-full bg-white border border-navy/10 rounded-2xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
              />
            </div>

            {/* Q3 */}
            <div className="mt-4">
              <label className="text-sm lg:text-base font-medium text-navy block leading-snug">
                3. What almost made you stop using the app?
              </label>
              <textarea
                data-testid="survey-q3"
                value={q3}
                onChange={(e) => setQ3(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Optional — even the smallest friction helps us fix it."
                className="mt-2 w-full bg-white border border-navy/10 rounded-2xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
              />
            </div>

            {/* Q4 */}
            <div className="mt-4">
              <label className="text-sm lg:text-base font-medium text-navy block leading-snug">
                4. What would make Brotherhood+ significantly better?
              </label>
              <textarea
                data-testid="survey-q4"
                value={q4}
                onChange={(e) => setQ4(e.target.value)}
                rows={3}
                maxLength={800}
                placeholder="Optional — a feature, a change, anything at all."
                className="mt-2 w-full bg-white border border-navy/10 rounded-2xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
              />
            </div>

            <div className="mt-6 flex flex-col-reverse lg:flex-row gap-3 lg:items-center lg:justify-between">
              <button data-testid="survey-dismiss" onClick={dismiss} className="text-sm text-navy/50 hover:text-navy">
                Ask me later
              </button>
              <button
                data-testid="survey-submit"
                onClick={submit}
                disabled={submitting || score === null}
                className="bg-navy text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-navy-soft transition-smooth disabled:opacity-50"
              >
                {submitting ? 'Sending…' : 'Submit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
